import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useEventStream, AccessEvent } from './useEventStream';
import { condoApi } from '../services/api';
import { logger } from '../services/logger';
import { isReplayedEvent } from '../utils/eventUtils';
import { getEventPhotoUrl } from '../utils/photoUtils';
import {
  getPersonPhoto,
  fetchPersonPhoto,
} from '../utils/personPhotoCache';

// Photo update queue for batching (performance optimization)
interface PhotoUpdate {
  eventId: string;
  photo: string;
  customerId?: string;
}
const photoUpdateQueue: PhotoUpdate[] = [];
let photoUpdateTimer: NodeJS.Timeout | null = null;

export interface OperationalEvent {
  id: string;
  type: 'entry' | 'exit' | 'alert' | 'system' | 'warning' | 'remote';
  description: string;
  location?: string;
  time: string;
  timestamp: string; // ISO timestamp for proper sorting
  status: 'success' | 'warning';
  customerId?: string;
  customerName?: string;
  personId?: string;
  personPhoto?: string;
  departmentName?: string; // For empresa
  unitName?: string; // For condominio (bloco + unit)
  streetName?: string; // Street name (shown on its own line)
  personType?: string; // Role type: resident, visitor, staff, etc.
  personRole?: string; // Human-readable role label: "Proprietário", "Familiar", "Visitante", etc.
  vehiclePlate?: string; // Vehicle plate for RFID vehicle events
  vehicleId?: string;   // UUID FK to vehicles table
  accessType?: string;  // 'rfid' or 'facial' — used to show tag icon vs face photo
  direction?: string;   // Raw DB direction: 'entrada', 'saida', 'passagem'
}

interface CustomerEventCache {
  events: OperationalEvent[];
  unreadCount: number;
}

interface UseGlobalEventStreamResult {
  isConnected: boolean;
  error: Error | null;
  getEventsForCustomer: (customerId: string) => OperationalEvent[];
  getUnreadCount: (customerId: string) => number;
  markAsRead: (customerId: string) => void;
  generalEvents: OperationalEvent[];
  reconnect: () => void;
  disconnect: () => void;
}

const MAX_EVENTS_PER_CUSTOMER = 50;
const MAX_GENERAL_EVENTS = 50;
const INITIAL_EVENTS_TO_LOAD = 10;

// Event types to hide from the right-side events panel (internal state-machine events)
const HIDDEN_EVENT_TYPES = new Set(['auto_check_in', 'auto_check_out']);

// Helper to compare arrays for equality (shallow)
const arraysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
};

// Helper to format event time - always shows date + time
const formatEventTime = (eventDate: Date): string => {
  return eventDate.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

// Transform AccessEvent to OperationalEvent (sync)
const transformAccessEvent = (event: AccessEvent): OperationalEvent => {
  // Determine event type based on direction and event_type
  let type: OperationalEvent['type'] = 'entry';
  if (event.event_type === 'remote_door_trigger' || event.event_type === 'remote_user') {
    type = 'remote';
  } else if (event.direction === 'saida' || event.event_type?.includes('exit')) {
    type = 'exit';
  } else if (event.event_type?.includes('alert') || event.event_type?.includes('denied')) {
    type = 'alert';
  } else if (event.event_type?.includes('system')) {
    type = 'system';
  }

  // Format time from ISO string - use processed_at for proper ordering
  const eventDate = new Date(event.processed_at || event.event_timestamp);
  const time = formatEventTime(eventDate);
  const timestamp = event.processed_at || event.event_timestamp; // Store ISO timestamp for sorting (prefer processed_at)

  // Build description
  let description = event.person_name || 'Desconhecido';
  if (type === 'remote') {
    description = `Abertura Remota - ${description}`;
  } else if (event.direction === 'passagem') {
    description = `Passagem - ${description}`;
  } else if (type === 'entry') {
    description = `Entrada - ${description}`;
  } else if (type === 'exit') {
    description = `Saída - ${description}`;
  } else if (type === 'alert') {
    description = event.is_authorized
      ? `Acesso autorizado - ${description}`
      : `Acesso negado - ${description}`;
  }

  // Use centralized photo selection utility
  const anyEvent = event as any;
  const personPhoto = getEventPhotoUrl(event);

  return {
    id: event.id,
    type,
    description,
    location: event.zone_name || event.device_name,
    time,
    timestamp,
    status: event.is_authorized ? 'success' : 'warning',
    customerId: event.customer_id,
    customerName: anyEvent.customer_name,
    personId: anyEvent.person_id,
    personPhoto,
    departmentName: anyEvent.department_name,
    unitName: anyEvent.unit_name,
    streetName: anyEvent.street_name,
    personType: anyEvent.person_type,
    personRole: anyEvent.person_role,
    vehiclePlate: anyEvent.vehicle_plate,
    vehicleId: anyEvent.vehicle_id,
    accessType: anyEvent.access_type,
    direction: event.direction,
  };
};

// Transform access log to OperationalEvent (sync - photo fetched separately)
const transformAccessLog = (log: any): OperationalEvent => {
  let type: OperationalEvent['type'] = 'entry';
  const eventType = log.event_type || '';
  const direction = log.direction || '';

  if (eventType === 'remote_door_trigger' || eventType === 'remote_user') {
    type = 'remote';
  } else if (direction === 'saida' || eventType.includes('exit')) {
    type = 'exit';
  } else if (eventType.includes('alert') || eventType.includes('denied')) {
    type = 'alert';
  } else if (eventType.includes('system')) {
    type = 'system';
  }

  const rawTimestamp = log.processed_at || log.event_timestamp || log.created_at;
  const eventDate = new Date(rawTimestamp);
  const time = formatEventTime(eventDate);
  const timestamp = rawTimestamp; // Store ISO timestamp for sorting (prefer processed_at)

  let description = log.person_name || 'Desconhecido';
  if (type === 'remote') {
    description = `Abertura Remota - ${description}`;
  } else if (direction === 'passagem') {
    description = `Passagem - ${description}`;
  } else if (type === 'entry') {
    description = `Entrada - ${description}`;
  } else if (type === 'exit') {
    description = `Saída - ${description}`;
  }

  // Use centralized photo selection utility
  const personPhoto = getEventPhotoUrl(log);

  return {
    id: log.id,
    type,
    description,
    location: log.zone_name || log.device_name,
    time,
    timestamp,
    status: log.is_authorized !== false ? 'success' : 'warning',
    customerId: log.customer_id,
    customerName: log.customer_name,
    personId: log.person_id,
    personPhoto,
    departmentName: log.department_name,
    unitName: log.unit_name,
    streetName: log.street_name,
    personType: log.person_type,
    personRole: log.person_role,
    vehiclePlate: log.vehicle_plate,
    vehicleId: log.vehicle_id,
    accessType: log.access_type,
    direction: log.direction,
  };
};

// Transform access log with photo fetching (async)
const transformAccessLogWithPhoto = async (log: any): Promise<OperationalEvent> => {
  const event = transformAccessLog(log);

  // If no photo URL from log fields but we have person_id, fetch the photo
  if (!event.personPhoto && log.person_id) {
    const photo = await fetchPersonPhoto(log.person_id);
    if (photo) {
      event.personPhoto = photo;
    }
  }

  return event;
};

export const useGlobalEventStream = (
  openCustomerIds: string[] = [],
  enabled: boolean = true,
  onNewRawEvent?: (event: AccessEvent) => void
): UseGlobalEventStreamResult => {
  // Cache events per customer
  const [eventCache, setEventCache] = useState<Map<string, CustomerEventCache>>(new Map());
  const [generalEvents, setGeneralEvents] = useState<OperationalEvent[]>([]);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);

  // Track which customers have had initial load
  const loadedCustomersRef = useRef<Set<string>>(new Set());

  // Track the previous customer IDs to avoid unnecessary updates
  const prevCustomerIdsRef = useRef<string[]>([]);

  // Keep callback in ref so it doesn't cause handleNewEvent to re-create
  const onNewRawEventRef = useRef(onNewRawEvent);
  useEffect(() => { onNewRawEventRef.current = onNewRawEvent; }, [onNewRawEvent]);

  // Function to process batched photo updates (performance optimization)
  const processBatchedPhotoUpdates = useCallback(() => {
    if (photoUpdateQueue.length === 0) return;

    // Copy queue and clear it
    const updates = [...photoUpdateQueue];
    photoUpdateQueue.length = 0;

    // Batch update customer events
    setEventCache((prev) => {
      const newCache = new Map(prev);
      updates.forEach(({ eventId, photo, customerId }) => {
        if (customerId) {
          const customerCache = newCache.get(customerId);
          if (customerCache) {
            const updatedEvents = customerCache.events.map((e) =>
              e.id === eventId ? { ...e, personPhoto: photo } : e
            );
            newCache.set(customerId, { ...customerCache, events: updatedEvents });
          }
        }
      });
      return newCache;
    });

    // Batch update general events
    setGeneralEvents((prev) =>
      prev.map((e) => {
        const update = updates.find(u => u.eventId === e.id);
        return update ? { ...e, personPhoto: update.photo } : e;
      })
    );
  }, []);

  // Schedule batched photo update processing
  const schedulePhotoUpdate = useCallback((update: PhotoUpdate) => {
    photoUpdateQueue.push(update);

    // Clear existing timer
    if (photoUpdateTimer) {
      clearTimeout(photoUpdateTimer);
    }

    // Process immediately if queue is large (10+ photos), otherwise wait 500ms
    if (photoUpdateQueue.length >= 10) {
      processBatchedPhotoUpdates();
    } else {
      photoUpdateTimer = setTimeout(() => {
        processBatchedPhotoUpdates();
        photoUpdateTimer = null;
      }, 500); // Batch updates every 500ms
    }
  }, [processBatchedPhotoUpdates]);

  // Use the existing event stream hook without customer filter for global events
  // Note: The backend may require a customer_id, so we'll use the first open customer
  // Memoize to avoid causing SSE reconnection on every render
  const primaryCustomerId = useMemo(() => {
    const newPrimary = openCustomerIds[0] || '';
    return newPrimary;
  }, [openCustomerIds.length > 0 ? openCustomerIds[0] : '']);

  const handleNewEvent = useCallback(async (event: AccessEvent) => {
    // Fire raw event callback (e.g. for toast notifications in OperationalView)
    onNewRawEventRef.current?.(event);

    // Skip automatic check-in/out events — they are noise in the events panel
    if (event.event_type && HIDDEN_EVENT_TYPES.has(event.event_type)) return;

    let transformedEvent = transformAccessEvent(event);
    const customerId = event.customer_id;
    const personId = (event as any).person_id;

    // Apply photo from cache immediately — avoids async state-update round-trip.
    // getPersonPhoto returns: string = cached URL, null = confirmed no photo, undefined = not yet fetched
    if (!transformedEvent.personPhoto && personId) {
      const cachedPhoto = getPersonPhoto(personId);
      if (cachedPhoto) {
        transformedEvent = { ...transformedEvent, personPhoto: cachedPhoto };
      }
    }

    // Add event (with photo already set if it was cached)
    if (customerId) {
      setEventCache((prev) => {
        const newCache = new Map(prev);
        const customerCache = newCache.get(customerId) || { events: [], unreadCount: 0 };
        const newEvents = [transformedEvent, ...customerCache.events].slice(0, MAX_EVENTS_PER_CUSTOMER);
        newCache.set(customerId, {
          events: newEvents,
          unreadCount: customerCache.unreadCount + 1,
        });
        return newCache;
      });
    }

    setGeneralEvents((prev) => {
      return [transformedEvent, ...prev].slice(0, MAX_GENERAL_EVENTS);
    });

    // Only fetch if not in cache yet (undefined = unknown, null = confirmed absent → skip)
    if (!transformedEvent.personPhoto && personId && getPersonPhoto(personId) === undefined) {
      fetchPersonPhoto(personId).then((photo) => {
        if (photo) {
          schedulePhotoUpdate({
            eventId: transformedEvent.id,
            photo,
            customerId,
          });
        }
      });
    }

    logger.debug('useGlobalEventStream', 'New event received', {
      eventId: event.id,
      customerId,
      type: transformedEvent.type,
    });
  }, [schedulePhotoUpdate]);

  const { isConnected, error, reconnect, disconnect } = useEventStream({
    customerId: primaryCustomerId,
    enabled: enabled && !!primaryCustomerId,
    onEvent: handleNewEvent,
  });

  // Load initial events for each open customer
  useEffect(() => {
    const loadInitialEvents = async (customerId: string) => {
      if (loadedCustomersRef.current.has(customerId)) return;

      try {
        const response = await condoApi.getAccessLogs({
          customer_id: customerId,
          limit: INITIAL_EVENTS_TO_LOAD,
          order: 'desc',
        });

        const rawLogs = response.data?.data || response.data || [];
        const logs = rawLogs.filter((log: any) => !log.event_type || !HIDDEN_EVENT_TYPES.has(log.event_type));
        // Use async photo fetching for initial events
        const events = await Promise.all(logs.map(transformAccessLogWithPhoto));

        // Update customer-specific cache
        setEventCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(customerId, {
            events,
            unreadCount: 0,
          });
          return newCache;
        });

        // Also add these events to general events (merged from all customers)
        setGeneralEvents((prev) => {
          // Add new events, avoiding duplicates by id
          const existingIds = new Set(prev.map(e => e.id));
          const newEvents = events.filter(e => !existingIds.has(e.id));
          // Merge and sort by timestamp (most recent first), keep max limit
          // NO age filter - user explicitly rejected 30-second filtering
          const merged = [...newEvents, ...prev]
            .sort((a, b) => {
              // Sort by ISO timestamp (date + time) for proper chronological order
              const tsA = a.timestamp || '';
              const tsB = b.timestamp || '';
              return tsB.localeCompare(tsA);
            })
            .slice(0, MAX_GENERAL_EVENTS);
          return merged;
        });

        loadedCustomersRef.current.add(customerId);
        logger.info('useGlobalEventStream', 'Loaded initial events', {
          customerId,
          count: events.length,
        });
      } catch (err) {
        logger.error('useGlobalEventStream', 'Failed to load initial events', err);
      }
    };

    // Load general system events (initial load only)
    const loadGeneralEvents = async () => {
      if (isInitialLoadDone) return;

      // Just set the system connected message initially
      // Real events will come from customer tabs
      const now = new Date();
      const systemEvents: OperationalEvent[] = [
        {
          id: 'sys-1',
          type: 'system',
          description: 'Sistema conectado',
          time: formatEventTime(now),
          timestamp: now.toISOString(),
          status: 'success',
        },
      ];

      setGeneralEvents(systemEvents);
      setIsInitialLoadDone(true);
    };

    // Always load general events on first render
    if (!isInitialLoadDone) {
      loadGeneralEvents();
    }

    // Check for changes in customer IDs
    if (!arraysEqual(openCustomerIds, prevCustomerIdsRef.current)) {
      const prevIds = new Set(prevCustomerIdsRef.current);
      const currentIds = new Set(openCustomerIds);

      // Find removed customers (closed tabs)
      const removedCustomerIds = Array.from(prevIds).filter(id => !currentIds.has(id));

      // Remove events from general events for closed tabs
      if (removedCustomerIds.length > 0) {
        setGeneralEvents((prev) => {
          return prev.filter(event => {
            // Keep events that don't have a customerId (system events)
            // or events whose customerId is still in the open tabs
            return !event.customerId || currentIds.has(event.customerId);
          });
        });

        // Also remove from cache and loadedCustomersRef
        removedCustomerIds.forEach(customerId => {
          loadedCustomersRef.current.delete(customerId);
          setEventCache((prev) => {
            const newCache = new Map(prev);
            newCache.delete(customerId);
            return newCache;
          });
        });

        logger.info('useGlobalEventStream', 'Removed events for closed tabs', {
          removedCustomerIds,
        });
      }

      // Load events for NEW customers (not already loaded)
      openCustomerIds.forEach(loadInitialEvents);

      prevCustomerIdsRef.current = [...openCustomerIds];
    }
  }, [openCustomerIds, isInitialLoadDone]);

  const getEventsForCustomer = useCallback((customerId: string): OperationalEvent[] => {
    return eventCache.get(customerId)?.events || [];
  }, [eventCache]);

  const getUnreadCount = useCallback((customerId: string): number => {
    return eventCache.get(customerId)?.unreadCount || 0;
  }, [eventCache]);

  const markAsRead = useCallback((customerId: string) => {
    setEventCache((prev) => {
      const newCache = new Map(prev);
      const customerCache = newCache.get(customerId);
      if (customerCache) {
        newCache.set(customerId, {
          ...customerCache,
          unreadCount: 0,
        });
      }
      return newCache;
    });
  }, []);

  return {
    isConnected,
    error,
    getEventsForCustomer,
    getUnreadCount,
    markAsRead,
    generalEvents,
    reconnect,
    disconnect,
  };
};
